import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_TableInstance,
} from "material-react-table";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { monospaceStyle } from "../../theme";
import { SourceEntry } from "../../types/productPlane";
import EntityRelationTable from "../Layout/EntityDetailOverlay/EntityRelationTable";
import EntityLink from "./EntityLink";
import SearchField from "./SearchField";
import { columnHeader, tableFrame } from "./claimCells";
import { useSourceJumpTarget } from "./sourceJump";

export interface SourceTableProps {
  // The numbered sources, as the response lists them
  sources: SourceEntry[];
  // Sources in the whole list, when the response holds only a part of it
  total?: number;
  // Rows per page
  pageSize?: number;
  mode?: "overlay" | "standalone";
}

// A source as a row of the relation table. `id` is what the table's
// "open in a new tab" action reads.
interface SourceRow extends Record<string, unknown> {
  id: string;
  number: number;
  title: string;
  site: string;
  date: string;
}

// A quiet cell: number, site and date step back behind the title. Set on
// the cell's content, not the cell, so that the highlight the table lays on
// a cell reaches every cell of the row.
const secondaryCell = (value: unknown) => (
  <Box component="span" sx={{ color: "text.secondary" }}>
    {String(value ?? "")}
  </Box>
);
const secondaryMonospaceCell = (value: unknown) => (
  <Box component="span" sx={{ ...monospaceStyle, color: "text.secondary" }}>
    {String(value ?? "")}
  </Box>
);

/**
 * The sources of a view: the existing relation table with number, title,
 * site and date, searchable and paged. A title opens the source document.
 *
 * It registers itself as the place the view's source marks lead to: asked
 * for a source, it turns to the page of its row and highlights the row.
 */
const SourceTable: React.FC<SourceTableProps> = ({
  sources,
  total,
  pageSize = 10,
  mode = "overlay",
}) => {
  const rows = useMemo<SourceRow[]>(
    () =>
      sources.map((source) => ({
        id: source.document_id,
        number: source.number,
        title: source.title || source.url || "Untitled source",
        site: source.site ?? "",
        date: source.date ?? "",
      })),
    [sources],
  );

  const columns = useMemo<MRT_ColumnDef<SourceRow>[]>(
    () => [
      {
        accessorKey: "number",
        header: "#",
        size: 70,
        enableColumnFilter: false,
        Cell: ({ cell }) => secondaryMonospaceCell(cell.getValue()),
      },
      {
        accessorKey: "title",
        header: "Title",
        size: 320,
        Cell: ({ row }) => (
          <EntityLink
            id={row.original.id}
            type="source"
            name={row.original.title}
          />
        ),
      },
      {
        accessorKey: "site",
        header: "Site",
        size: 200,
        Cell: ({ cell }) => secondaryCell(cell.getValue()),
      },
      {
        accessorKey: "date",
        header: "Date",
        size: 120,
        Cell: ({ cell }) => secondaryMonospaceCell(cell.getValue()),
      },
    ],
    [],
  );

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [globalFilter, setGlobalFilter] = useState<string | undefined>();
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );

  // The source whose row is marked, and the one still to be brought into view
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const [wanted, setWanted] = useState<number | null>(null);

  const tableRef = useRef<MRT_TableInstance<SourceRow> | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const showRow = useCallback((sourceNumber: number) => {
    setHighlighted(sourceNumber);
    setWanted(sourceNumber);
  }, []);
  useSourceJumpTarget(showRow);

  // Brings the wanted row into view, a step per render: lift a search that
  // hides it, turn to its page, then scroll to it.
  useEffect(() => {
    const table = tableRef.current;
    if (wanted === null || !table) {
      return;
    }
    const index = table
      .getPrePaginationRowModel()
      .rows.findIndex((row) => row.original.number === wanted);
    if (index < 0) {
      const searched = Boolean(globalFilter) || columnFilters.length > 0;
      if (searched) {
        setGlobalFilter(undefined);
        setColumnFilters([]);
      } else {
        setWanted(null);
      }
      return;
    }
    const pageIndex = Math.floor(index / pagination.pageSize);
    if (pageIndex !== pagination.pageIndex) {
      setPagination({ ...pagination, pageIndex });
      return;
    }
    const row = boxRef.current?.querySelector('tr[aria-current="true"]');
    // Not every environment scrolls: the test environment does not
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    setWanted(null);
  }, [wanted, pagination, globalFilter, columnFilters]);

  if (rows.length === 0) {
    return null;
  }

  // Drawn as every table of a detail view: the search above the frame, the
  // heads in small capitals, no column menus, a pager of one page size
  return (
    <Box ref={boxRef} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex" }}>
        <SearchField
          value={globalFilter ?? ""}
          onChange={(value) => setGlobalFilter(value || undefined)}
          label="Search sources"
        />
      </Box>
      <EntityRelationTable<SourceRow>
        columns={columns}
        data={rows}
        pageSize={pageSize}
        context="source"
        mode={mode}
        minHeight={0}
        tableOptions={{
          enableTopToolbar: false,
          enableColumnActions: false,
          enableColumnFilters: false,
          enableDensityToggle: false,
          enableFullScreenToggle: false,
          renderBottomToolbarCustomActions: () => null,
          enableBottomToolbar: rows.length > pagination.pageSize,
          getRowId: (row) => String(row.number),
          tableInstanceRef: tableRef,
          initialState: { density: "compact" },
          state: { pagination, globalFilter, columnFilters },
          onPaginationChange: setPagination,
          onGlobalFilterChange: setGlobalFilter,
          onColumnFiltersChange: setColumnFilters,
          muiTablePaginationProps: {
            rowsPerPageOptions: [pagination.pageSize],
            showFirstButton: false,
            showLastButton: false,
          },
          muiTablePaperProps: { elevation: 0, sx: tableFrame },
          muiTableHeadCellProps: { sx: columnHeader },
          muiBottomToolbarProps: {
            sx: { backgroundColor: "background.raised", boxShadow: "none" },
          },
          muiTableProps: {
            sx: {
              tableLayout: "fixed",
              "& .MuiTableHead-root .MuiTableCell-root, & .MuiTableBody-root .MuiTableRow-root":
                { backgroundColor: "background.raised" },
              "& .MuiTableBody-root .MuiTableCell-root": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            },
          },
          muiTableBodyRowProps: ({ row }) =>
            row.original.number === highlighted ? { "aria-current": true } : {},
          muiTableBodyCellProps: ({ row }) =>
            row.original.number === highlighted
              ? {
                  // Laid over the cell's own surface, which the table sets
                  sx: {
                    boxShadow: (theme) =>
                      `inset 0 0 0 999px ${alpha(theme.palette.link.main, 0.16)}`,
                  },
                }
              : {},
        }}
      />
      {total !== undefined && total > rows.length && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ...monospaceStyle, display: "block", px: 2, pb: 1 }}
        >
          {rows.length} of {total} sources
        </Typography>
      )}
    </Box>
  );
};

export default SourceTable;
